/** "12h" said as a person would say it. The value itself is still shown; this is the
 *  reassurance underneath it, for whoever does not read durations fluently. */
function durationInWords(value: string): string {
  // The unit is optional only so that a bare "0" — how the server writes an
  // off duration, with no h/m/s after it (config/settings.go's shortDuration) —
  // is read the same way count and days read their own off value, rather than
  // being a duration this function claims it cannot read.
  const match = /^(\d+)(h|m|s)?$/.exec(value.trim())
  if (!match) return ''
  const n = Number(match[1])
  if (n === 0) return 'off'
  if (!match[2]) return ''
  const symbol = match[2] as 'h' | 'm' | 's'
  const unit = { h: 'hour', m: 'minute', s: 'second' }[symbol]
  if (symbol === 'h' && n % 24 === 0) {
    const days = n / 24
    return `${days} day${days === 1 ? '' : 's'}`
  }
  return `${n} ${unit}${n === 1 ? '' : 's'}`
}

/** A setting's value said as a person would say it, under the box it is typed in.
 *  Empty when the value cannot be read: the box's own validation says why. */
export function settingInWords(kind: string, value: string): string {
  const v = value.trim()
  if (kind === 'duration') return durationInWords(v)
  if (kind === 'count' || kind === 'days') {
    if (!/^\d+$/.test(v)) return ''
    const n = Number(v)
    if (n === 0) return 'off'
    return kind === 'count' ? `after ${n} failed sign-in${n === 1 ? '' : 's'}` : `${n} day${n === 1 ? '' : 's'}`
  }
  return ''
}

/**
 * What is wrong with a value before it is sent, or '' when nothing is.
 *
 * The same rules as the server's (config.Problems and authProblems), one value at a
 * time: a duration Go can parse, a whole number, and the floors below which a
 * setting catches the innocent (one mistyped password, a fortnight's leave). The
 * rules between two settings stay the server's; its refusal is shown beside the box.
 */
export function settingCheck(kind: string, value: string): string {
  const v = value.trim()
  if (v === '') return 'Enter a value; 0 is off.'
  if (kind === 'duration') {
    return /^(0|(\d+(\.\d+)?(ns|us|µs|ms|s|m|h))+)$/.test(v) ? '' : 'Use h, m or s, like 12h; 0 is off.'
  }
  if (kind === 'count' || kind === 'days') {
    if (!/^\d+$/.test(v)) return 'Whole number; 0 is off.'
    const n = Number(v)
    if (kind === 'count' && n > 0 && n < 3) return 'At least 3, or 0 for off.'
    if (kind === 'days' && n > 0 && n < 30) return 'At least 30, or 0 for off.'
  }
  return ''
}
